import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { listOwnFeedback } from './api';
import './Feedbacks.css';

// The date a submission was made, in the reader's language. The time of day is noise on a
// receipt, so only the date is shown; the machine-readable instant stays on the <time> element.
const formatDate = (isoDate, language) => new Date(isoDate).toLocaleDateString(language);

// A stored row carries whatever currency code was accepted when it was written, and Intl's
// currency style throws on a code it does not know, so the code is printed beside the figure.
// At least two decimals, at most four — the column keeps four, and a receipt that rounds is a
// receipt that disagrees with the record.
const formatAmount = (amount, currency, language) => {
  const figure = amount.toLocaleString(language, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
  // Every field of a submission is optional, so an amount can arrive with no currency.
  return currency ? `${figure} ${currency}` : figure;
};

// What the user has already told us, newest first. A receipt, not a dossier: the date, the amount
// as it was given, and the comment — never the email, the device, or what their usage cost us.
// Nothing here edits or withdraws a submission; feedback is append-only.
function Feedbacks() {
  const { t, i18n } = useTranslation();
  const [submissions, setSubmissions] = useState([]);
  // True until the read settles. An unanswered read and an empty one look identical, and asking
  // a user who has already written to us to write again is how duplicates get into a record
  // that keeps every copy.
  const [loading, setLoading] = useState(true);
  // Set when the history could not be read. Silence here would read as "you never said
  // anything", which is the one answer this screen must never give by accident.
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let abandoned = false;

    listOwnFeedback()
      .then(({ submissions: own }) => {
        if (!abandoned) setSubmissions(own || []);
      })
      .catch(() => {
        if (!abandoned) setUnavailable(true);
      })
      .finally(() => {
        if (!abandoned) setLoading(false);
      });

    return () => {
      abandoned = true;
    };
  }, []);

  return (
    <div className="feedbacks">
      <h1>{t('feedback.historyTitle')}</h1>

      {loading && <div className="chat-list-status">{t('feedback.loading')}</div>}

      {!loading && unavailable && (
        <p className="feedback-history-unavailable">{t('feedback.historyUnavailable')}</p>
      )}

      {!loading && !unavailable && submissions.length === 0 && (
        <div className="feedbacks-invitation">
          <p>{t('feedback.nothingYet')}</p>
          <Link className="feedbacks-invitation-cta" to="/feedback">
            {t('feedback.give')}
          </Link>
        </div>
      )}

      {!loading && (unavailable || submissions.length > 0) && (
        <Link className="feedbacks-more" to="/feedback">
          {t('feedback.give')}
        </Link>
      )}

      {submissions.length > 0 && (
        <ul className="feedback-history-list">
          {submissions.map((submission, index) => (
            // Two submissions can share a timestamp — the server orders on the row id to break
            // exactly that tie — and the receipt carries no id to key on, so the position joins
            // the date to keep the keys unique.
            <li className="feedback-history-entry" key={`${submission.createdAt}#${index}`}>
              <div className="feedback-history-head">
                <time dateTime={submission.createdAt}>
                  {formatDate(submission.createdAt, i18n.language)}
                </time>
                {submission.amount != null && (
                  <span className="feedback-history-amount">
                    {formatAmount(submission.amount, submission.currency, i18n.language)}
                  </span>
                )}
              </div>
              {submission.comment ? (
                <p className="feedback-history-comment">{submission.comment}</p>
              ) : (
                submission.amount == null && (
                  <p className="feedback-history-empty">{t('feedback.historyNothingSaid')}</p>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Feedbacks;
